// Wave crests — pencil marks on the sea. Each patch of water gets its own height and direction: the offshore
// swell and wind sea from the model, reduced by the exposure atlas (how much open water lies up-wave of that
// patch), so the channel behind Tortola goes quiet while the Atlantic shores stay busy. Crests are spaced by the
// deep-water wavelength, march at phase speed, and bend to run parallel to a nearby shore.
export class WaveLayer {
  constructor(map, canvas, islands, exposure) {
    this.map = map; this.canvas = canvas; this.ctx = canvas.getContext('2d'); this.islands = islands; this.exp = exposure;
    this.field = null; this.step = 0; this.enabled = false; this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.cells = null; this.coast = null; this.phase = 0; this._last = performance.now();
    this.resize();
    map.on('move', () => { this.cells = null; this.coast = null; });
    map.on('resize', () => this.resize());
  }
  resize() {
    const r = this.map.getContainer().getBoundingClientRect(); this.w = r.width; this.h = r.height;
    this.canvas.width = Math.round(this.w * this.dpr); this.canvas.height = Math.round(this.h * this.dpr);
    this.canvas.style.width = this.w + 'px'; this.canvas.style.height = this.h + 'px';
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0); this.cells = null; this.coast = null;
  }
  setField(f) { this.field = f; this.cells = null; }
  setStep(s) { this.step = s; this.cells = null; }
  setEnabled(on) { this.enabled = on; if (!on) this.ctx.clearRect(0, 0, this.w, this.h); }
  mpp() { const z = this.map.getZoom(), lat = this.map.getCenter().lat; return 156543.03 * Math.cos(lat * Math.PI / 180) / Math.pow(2, z); }
  buildCoast() {
    const path = new Path2D(); const verts = []; const pad = 200;
    for (const f of this.islands.features) {
      const ring = f.geometry.coordinates[0]; let first = true;
      for (const [lon, lat] of ring) {
        const s = this.map.project([lon, lat]);
        if (first) path.moveTo(s.x, s.y); else path.lineTo(s.x, s.y); first = false;
        if (s.x > -pad && s.x < this.w + pad && s.y > -pad && s.y < this.h + pad && verts.length < 12000) verts.push(s.x, s.y);
      }
      path.closePath();
    }
    this.coast = { path, verts };
  }
  buildCells() {
    const z = this.map.getZoom(); const size = z < 10 ? 84 : z < 12 ? 100 : 128;
    const cols = Math.ceil(this.w / size) + 1, rows = Math.ceil(this.h / size) + 1;
    const mpp = this.mpp(); const cells = []; const verts = this.coast.verts;
    const reach = 900 / mpp;
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const x = c * size, y = r * size; const ll = this.map.unproject([x + size / 2, y + size / 2]);
      const sea = this.exp.seaAt(this.field, ll.lng, ll.lat, this.step);
      const useSwell = sea.sh >= sea.wsea * 0.8; const dirFrom = useSwell ? sea.sd : sea.wd; const H = sea.wh;
      const T = useSwell ? sea.sp : Math.max(3, sea.wp * 0.8);
      let dir = (dirFrom + 180) * Math.PI / 180;
      let best = reach * reach, nx = 0, ny = 0;
      for (let i = 0; i < verts.length; i += 2) { const dx = verts[i] - x, dy = verts[i + 1] - y; const d2 = dx * dx + dy * dy; if (d2 < best) { best = d2; nx = dx; ny = dy; } }
      if (best < reach * reach) {
        const wgt = 1 - Math.sqrt(best) / reach; const toShore = Math.atan2(nx, -ny);
        let dd = toShore - dir; dd = Math.atan2(Math.sin(dd), Math.cos(dd));
        if (Math.abs(dd) < Math.PI * 0.56) dir += dd * wgt * 0.8;
      }
      const Lm = 1.56 * T * T / mpp; const L = Math.max(26, Math.min(140, Lm));
      const seed = ((r * 73856093) ^ (c * 19349663)) >>> 0; const jit = ((seed % 1000) / 1000 - 0.5) * 0.5 * L; const lenv = 0.7 + ((seed >> 4) % 100) / 300;
      cells.push({ x, y, size, dir, H, L, T, jit, lenv, tiny: Lm < 26 });
    }
    this.cells = cells;
  }
  frame(now) {
    if (!this.enabled || !this.field || !this.exp.bands) return;
    const dt = Math.min(100, now - this._last); this._last = now;
    if (!this.coast) this.buildCoast();
    if (!this.cells) this.buildCells();
    const ctx = this.ctx; ctx.clearRect(0, 0, this.w, this.h); ctx.save();
    const mask = new Path2D(); mask.rect(-1, -1, this.w + 2, this.h + 2); mask.addPath(this.coast.path); ctx.clip(mask, 'evenodd');
    this.phase += dt * 0.014; ctx.lineCap = 'round';
    for (const cell of this.cells) {
      if (cell.H < 0.15) continue;
      // graphite marks: heavier with height; a calm bay gets almost nothing
      const a = Math.min(0.7, 0.05 + cell.H * 0.3) * (cell.tiny ? 0.5 : 1);
      ctx.strokeStyle = `rgba(58,64,74,${a})`; ctx.lineWidth = Math.min(1.8, 0.7 + cell.H * 0.4);
      const cx = cell.x + cell.size / 2, cy = cell.y + cell.size / 2;
      const ax = Math.sin(cell.dir), ay = -Math.cos(cell.dir); const px = -ay, py = ax;
      const half = cell.size * 0.62, len = cell.size * 0.34 * cell.lenv * Math.min(1, 0.5 + cell.H * 0.5);
      const off = ((this.phase / cell.L) % 1) * cell.L + cell.jit;
      for (let k = -half; k <= half; k += cell.L) {
        const d = k + off; const x0 = cx + ax * d, y0 = cy + ay * d;
        const fade = 1 - Math.min(1, Math.abs(d) / (half + 1)); if (fade <= 0.05) continue;
        ctx.globalAlpha = fade;
        ctx.beginPath(); ctx.moveTo(x0 - px * len, y0 - py * len); ctx.quadraticCurveTo(x0 + ax * 3, y0 + ay * 3, x0 + px * len, y0 + py * len); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1; ctx.restore();
  }
}
